import { useApolloClient, useMutation } from "@apollo/client";

import {
  BULK_DELETE_OWN_OEM_TICKETS_MUTATION,
  UPDATE_OWN_OEM_TICKET_MUTATION,
  CREATE_OWN_OEM_TICKET_MUTATION,
  Ticket,
  ADD_OWN_OEM_PART_TO_TICKET_MUTATION,
  ADD_TICKET_TIME_TRACKER_LOG_MUTATION,
  NOTIFY_ON_INTERNAL_POST_MUTATION,
  REMOVE_OWN_OEM_PART_FROM_TICKET_MUTATION,
  EDIT_OWN_OEM_PART_ON_TICKET_MUTATION,
  REMOVE_TIME_TRACKER_LOG_FROM_TICKET_MUTATION,
  UPDATE_TICKET_TIME_TRACKER_LOG_MUTATION,
  UPDATE_TICKET_KANBAN_OWN_OEM_TICKET_MUTATION,
  GET_OWN_OEM_TICKET_BY_ID,
  ADD_TICKET_ASSIGNEE_MUTATION,
  REMOVE_TICKET_ASSIGNEE_MUTATION,
  ADD_TICKET_FOLLOWER_MUTATION,
  REMOVE_TICKET_FOLLOWER_MUTATION,
  DELETE_OWN_OEM_TICKET_MUTATION,
  ARCHIVE_OWN_OEM_TICKET_MUTATION,
  UNARCHIVE_OWN_OEM_TICKET_MUTATION,
  UPDATE_TITLE_TICKET_MUTATION,
  UPDATE_DESCRIPTION_TICKET_MUTATION,
  ADD_TICKET_RESOURCE_MUTATION,
  REMOVE_TICKET_RESOURCES_MUTATION,
  LINK_TICKETS,
  UNLINK_TICKETS,
  ADD_TICKET_ATTACHMENT_MUTATION,
  REMOVE_TICKET_ATTACHMENT_MUTATION,
} from "../../api";
import client from "../../apollo/_client";

/** @deprecated Use `useUpdateWorkOrder` from `~/services/workOrders`. */
export const updateTicket = () => {
  const [updateOwnOemTicket, { error, loading }] = useMutation(
    UPDATE_OWN_OEM_TICKET_MUTATION,
    {
      refetchQueries: ["getOwnOemTicketById"],
      awaitRefetchQueries: true,
    },
  );

  return {
    mutation: (data, { skipCacheUpdate = false } = {}) =>
      updateOwnOemTicket({
        variables: {
          input: {
            ...data,
          },
        },
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,

        update(cache, { data: { updateOwnOemTicket: ref } }) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();

          if (skipCacheUpdate) {
            return;
          }
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              listOwnOemOpenTickets(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketListData,
                  fragmentName: "TicketListData",
                });

                return [newRef, ...existingRefs];
              },
            },
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemAllTickets",
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemOpenTickets",
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemTickets",
          });

          if (data.status) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getOwnOemAnalytics",
            });
          }
          cache.gc();
        },
      }),
    loading,
    error,
  };
};

/** @deprecated Use `useAddWorkOrderResource` from `~/services/workOrders`. */
export const addResource = () => {
  const [addTicketResource, { error, loading }] = useMutation(
    ADD_TICKET_RESOURCE_MUTATION,
  );

  return {
    addResourceMutation: (data) =>
      addTicketResource({
        variables: {
          input: {
            ...data,
          },
        },
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,

        update(cache, { data: { addTicketResource: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [newRef, ...existingRefs];
              },
            },
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemTickets",
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemResources",
          });

          cache.gc();
        },
      }),
    loading,
    error,
  };
};

/** @deprecated Use `useRemoveWorkOrderResources` from `~/services/workOrders`. */
export const removeResources = () => {
  const [removeTicketResources, { error, loading }] = useMutation(
    REMOVE_TICKET_RESOURCES_MUTATION,
  );

  return {
    removeResourcesMutation: (data) =>
      removeTicketResources({
        variables: {
          input: {
            ...data,
          },
        },
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,

        update(cache, { data: { removeTicketResources: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [newRef, ...existingRefs];
              },
            },
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemTickets",
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemResources",
          });

          cache.gc();
        },
      }),
    loading,
    error,
  };
};

export const createTicket = () => {
  const [createOwnOemTicket, { error: { networkError } = {}, loading }] =
    useMutation(CREATE_OWN_OEM_TICKET_MUTATION);

  return {
    mutation: (data) =>
      createOwnOemTicket({
        variables: {
          input: {
            ...data,
          },
        },
        update(cache, { data: { createOwnOemTicket: ref } }) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemKanbanTickets",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemTickets",
          });
          // Creating a work order writes activity-log rows on the resources it
          // touches — including the "created a Work Order … on this ticket" row
          // on a support ticket when the WO is created from one. The Activity
          // Log tab watches `listOwnOemActivityLogs`, which nothing else here
          // invalidates, so without this the new row only appears on refresh.
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();
        },
      }),
    loading,
    serverErrors: networkError,
  };
};

export const notifyOnInternalPost = () => {
  const [notifyOnInternalNotePost, { error: { networkError } = {}, loading }] =
    useMutation(NOTIFY_ON_INTERNAL_POST_MUTATION);

  return {
    mutation: ({ ticketId, mentionedUsers, message }) =>
      notifyOnInternalNotePost({
        variables: {
          input: {
            ticketId,
            mentionedUsers,
            message,
          },
        },
      }),
    loading,
    serverErrors: networkError,
  };
};

/** @deprecated Use `useAssignWorkOrderPart` from `~/services/workOrders`. */
export const assignPart = () => {
  const [assign, { loading: assigningPart, error }] = useMutation(
    ADD_OWN_OEM_PART_TO_TICKET_MUTATION,
    {
      refetchQueries: ["getOwnOemTicketById"],
      awaitRefetchQueries: true,
    },
  );

  return {
    assigningPart,
    error,
    assignPartToTicket: async (input) =>
      assign({
        variables: { input },
        skip: !input?.ticketId || !input?.inventoryParts?.length,
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
        update(cache, { data: { assignOwnOemInventoryPartsToTicket: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
        },
      }),
  };
};

/** @deprecated Use `useRemoveWorkOrderPart` from `~/services/workOrders`. */
export const removePart = () => {
  const [remove, { loading: removingPart, error }] = useMutation(
    REMOVE_OWN_OEM_PART_FROM_TICKET_MUTATION,
    {
      refetchQueries: ["getOwnOemTicketById"],
      awaitRefetchQueries: true,
    },
  );

  return {
    removingPart,
    error,
    removePartFromTicket: async (input) =>
      remove({
        variables: { input },
        skip: !input?.ticketId || !input?.partId,
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
        update(cache, { data: { removeOwnOemInventoryPartFromTicket: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
        },
      }),
  };
};

/** @deprecated Use `useUpdateWorkOrderPartQuantity` from `~/services/workOrders`. */
export const updatePartQuantity = () => {
  const [update, { loading: updatingPart, error }] = useMutation(
    EDIT_OWN_OEM_PART_ON_TICKET_MUTATION,
    {
      refetchQueries: ["getOwnOemTicketById"],
      awaitRefetchQueries: true,
    },
  );

  return {
    updatingPart,
    error,
    updatePartQuantity: async (input) =>
      update({
        variables: { input },
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
        update(cache, { data: { editOwnOemInventoryPartOnTicket: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
        },
      }),
  };
};

export const addTimeTrackerLog = () => {
  const [add, { loading: addingTimeLog, error }] = useMutation(
    ADD_TICKET_TIME_TRACKER_LOG_MUTATION,
  );

  return {
    addingTimeLog,
    error,
    addTimeLog: async (input) =>
      add({
        variables: { input },
        skip:
          !input?.ticketId || !Object.keys(input?.timeTrackerLog || {}).length,
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
        update(cache, { data: { addTimeTrackerLog: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();
        },
      }),
  };
};

export const updateTimeTrackerLog = () => {
  const [update, { loading: updatingTimeLog, error }] = useMutation(
    UPDATE_TICKET_TIME_TRACKER_LOG_MUTATION,
  );

  return {
    updatingTimeLog,
    updateTimeLogError: error,
    updateTimeLog: async (input) =>
      update({
        variables: { input },
        skip: !input?.ticketId || !input.timeLogId,
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
        update(cache, { data: { updateTimeTrackerLog: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
        },
      }),
  };
};

export const removeTimeTrackerLog = () => {
  const [remove, { loading: removingTimeLog, error }] = useMutation(
    REMOVE_TIME_TRACKER_LOG_FROM_TICKET_MUTATION,
  );

  return {
    removingTimeLog,
    error,
    removeTimeLogFromTicket: async (input) =>
      remove({
        variables: { input },
        skip: !input?.ticketId || !input?.timeLogId,
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
        update(cache, { data: { removeTimeTrackerLogFromTicket: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
        },
      }),
  };
};

/** @deprecated Use `useUpdateWorkOrderTitle` from `~/services/workOrders`. */
export const updateTitleTicket = () => {
  const [update, { loading: updatingTitle, error }] = useMutation(
    UPDATE_TITLE_TICKET_MUTATION,
  );

  return {
    updatingTitle,
    updateTitleError: error,
    updateTitle: async (input) =>
      update({
        variables: { input },
        skip: !input?.ticketId || !input.title,
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
        update(cache, { data: { updateTitleTicket: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();
        },
      }),
  };
};

/** @deprecated Use `useUpdateWorkOrderDescription` from `~/services/workOrders`. */
export const updateDescriptionTicket = () => {
  const [update, { loading: updatingDescription, error }] = useMutation(
    UPDATE_DESCRIPTION_TICKET_MUTATION,
  );

  return {
    updatingDescription,
    updateDescriptionError: error,
    updateDescription: async (input) =>
      update({
        variables: { input },
        skip: !input?.ticketId || !input.description,
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
        update(cache, { data: { updateDescriptionTicket: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Ticket", id: ref._id }),
            fields: {
              getOwnOemTicketById(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Ticket.fragments.TicketSingleData,
                  fragmentName: "TicketSingleData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();
        },
      }),
  };
};

export const useWorkOrderKanbanMutation = () => {
  const [update] = useMutation(UPDATE_TICKET_KANBAN_OWN_OEM_TICKET_MUTATION, {
    refetchQueries: ["listOwnOemAllTickets"],
  });

  return {
    workOrderKanbanMutation: async (input) => {
      return update({
        variables: {
          input,
        },
      });
    },
  };
};

export const updateTicketCacheOnProcedureAttached = async (
  id,
  procedure,
  addedBy,
) => {
  const ticket = client.readQuery({
    query: GET_OWN_OEM_TICKET_BY_ID,
    variables: { id },
  });
  if (!ticket) return false;

  const { getOwnOemTicketById: cachedTicket } = ticket;
  if (cachedTicket) {
    const cloned = JSON.parse(JSON.stringify(cachedTicket));
    cloned.procedures = cloned.procedures || [];
    cloned.procedures.push({
      procedure,
      addedBy,
    });

    client.writeQuery({
      query: GET_OWN_OEM_TICKET_BY_ID,
      variables: { id },
      data: {
        getOwnOemTicketById: cloned,
      },
    });
    return true;
  }

  return false;
};

/** @deprecated Use `useAddWorkOrderAssignee` from `~/services/workOrders`. */
export const useAddTicketAssignee = () => {
  const [addTicketAssignee, { error, loading }] = useMutation(
    ADD_TICKET_ASSIGNEE_MUTATION,
  );

  return {
    mutation: (data) =>
      addTicketAssignee({
        variables: {
          input: {
            ...data,
          },
        },
        refetchQueries: ["getOwnOemTicketById"],
        awaitRefetchQueries: true,
      }),
    loading,
    error,
  };
};

/** @deprecated Use `useAddWorkOrderAttachments` from `~/services/workOrders`. */
export const useAddTicketAttachments = () => {
  const [addTicketAttachments, { error, loading }] = useMutation(
    ADD_TICKET_ATTACHMENT_MUTATION,
  );

  return {
    mutation: (data) =>
      addTicketAttachments({
        variables: {
          input: {
            ...data,
          },
        },
      }),
    loading,
    error,
  };
};

/** @deprecated Use `useRemoveWorkOrderAttachment` from `~/services/workOrders`. */
export const useRemoveTicketAttachment = () => {
  const [removeTicketAttachment, { error, loading }] = useMutation(
    REMOVE_TICKET_ATTACHMENT_MUTATION,
  );

  return {
    mutation: ({ ticketId, attachmentId, ...rest }) =>
      removeTicketAttachment({
        variables: {
          input: {
            ticketId,
            attachmentId,
            ...rest,
          },
        },
        update(cache) {
          try {
            const { getOwnOemTicketById } = cache.readQuery({
              query: GET_OWN_OEM_TICKET_BY_ID,
              variables: { id: ticketId },
            });

            if (!getOwnOemTicketById) return;

            const cloned = { ...getOwnOemTicketById };
            cloned.attachments = cloned.attachments.filter(
              (attachment) => attachment._id !== attachmentId,
            );

            cache.writeQuery({
              query: GET_OWN_OEM_TICKET_BY_ID,
              variables: { id: ticketId },
              data: {
                getOwnOemTicketById: cloned,
              },
            });

            cache.gc();
          } catch (error) {
            console.error("Cache update error:", error);
          }
        },
      }),
    loading,
    error,
  };
};

/** @deprecated Use `useRemoveWorkOrderAssignee` from `~/services/workOrders`. */
export const useRemoveTicketAssignee = () => {
  const [removeTicketAssignee, { error, loading }] = useMutation(
    REMOVE_TICKET_ASSIGNEE_MUTATION,
  );

  return {
    mutation: (data) =>
      removeTicketAssignee({
        variables: {
          input: {
            ...data,
          },
        },
      }),
    loading,
    error,
  };
};

/** @deprecated Use `useAddWorkOrderFollower` from `~/services/workOrders`. */
export const useAddTicketFollower = () => {
  const [addTicketFollower, { error, loading }] = useMutation(
    ADD_TICKET_FOLLOWER_MUTATION,
  );

  return {
    mutation: (data) =>
      addTicketFollower({
        variables: {
          input: {
            ...data,
          },
        },
        update(cache, { data }) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();
        },
      }),
    loading,
    error,
  };
};

/** @deprecated Use `useRemoveWorkOrderFollower` from `~/services/workOrders`. */
export const useRemoveTicketFollower = () => {
  const [removeTicketFollower, { error, loading }] = useMutation(
    REMOVE_TICKET_FOLLOWER_MUTATION,
  );

  return {
    mutation: (data) =>
      removeTicketFollower({
        variables: {
          input: {
            ...data,
          },
        },
        update(cache, { data }) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();
        },
      }),
    loading,
    error,
  };
};

/** @deprecated Use `useLinkWorkOrders` from `~/services/workOrders`. */
export const useLinkWorkOrders = () => {
  const [linkWorkOrdersFetched, { loading, error }] = useMutation(LINK_TICKETS);

  return {
    mutation: (input) =>
      linkWorkOrdersFetched({
        variables: input,
      }),
    loading,
    error,
  };
};

export const useUnLinkWorkOrders = () => {
  const [unLinkWorkOrdersFetched, { loading, error }] =
    useMutation(UNLINK_TICKETS);

  return {
    mutation: (input) =>
      unLinkWorkOrdersFetched({
        variables: input,
      }),
    loading,
    error,
  };
};

export const useDeleteOwnOemTicket = () => {
  const [remove, { loading, error }] = useMutation(
    DELETE_OWN_OEM_TICKET_MUTATION,
  );

  return {
    loading,
    error,
    removeTicket: async (data) =>
      new Promise((resolve, reject) =>
        remove({
          variables: {
            input: {
              ...data,
            },
          },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemTickets",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemOpenTickets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          }),
      ),
  };
};

export const useArchiveOwnOemTicket = () => {
  const [archiveTicket, { loading, error }] = useMutation(
    ARCHIVE_OWN_OEM_TICKET_MUTATION,
    {
      refetchQueries: ["getOwnOemTicketById", "listOwnOemTickets"],
      awaitRefetchQueries: true,
    },
  );

  return {
    loading,
    error,
    archiveTicket: (input) =>
      archiveTicket({
        variables: { input: { ticketId: input.ticketId } },
        update(cache) {
          cache.evict({ id: "ROOT_QUERY", fieldName: "listOwnOemTickets" });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemArchivedTickets",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();
        },
      }),
  };
};

export const useUnarchiveOwnOemTicket = () => {
  const [unarchiveTicket, { loading, error }] = useMutation(
    UNARCHIVE_OWN_OEM_TICKET_MUTATION,
    {
      refetchQueries: ["getOwnOemTicketById", "listOwnOemTickets"],
      awaitRefetchQueries: true,
    },
  );

  return {
    loading,
    error,
    unarchiveTicket: (input) =>
      unarchiveTicket({
        variables: { input: { ticketId: input.ticketId } },
        update(cache) {
          cache.evict({ id: "ROOT_QUERY", fieldName: "listOwnOemTickets" });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemArchivedTickets",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemActivityLogs",
          });
          cache.gc();
        },
      }),
  };
};

const evictTicketListCaches = (cache) => {
  [
    "listOwnOemTickets",
    "listOwnOemOpenTickets",
    "listOwnOemArchivedTickets",
    "listOwnOemKanbanTickets",
    "listOwnOemActivityLogs",
  ].forEach((fieldName) => cache.evict({ id: "ROOT_QUERY", fieldName }));
  cache.gc();
};

export const useBulkArchiveTickets = () => {
  const client = useApolloClient();
  const [archiveTicket] = useMutation(ARCHIVE_OWN_OEM_TICKET_MUTATION);

  // Archive one work order at a time (sequentially): parent archive cascades
  // to subs, so we skip selected children of selected parents to avoid
  // spurious partial failures. Evict list caches once after the loop.
  const bulkArchiveTickets = async (ticketIds, ticketsWithSubs = []) => {
    const succeeded = [];
    const failed = [];
    const idSet = new Set(ticketIds);
    const cascadedChildIds = new Set();

    ticketsWithSubs.forEach((t) => {
      if (!idSet.has(t._id)) return;
      (t.subWorkOrders || []).forEach((sub) => {
        if (sub?._id && idSet.has(sub._id)) cascadedChildIds.add(sub._id);
      });
    });

    const idsToArchive = ticketIds.filter((id) => !cascadedChildIds.has(id));

    for (const ticketId of idsToArchive) {
      try {
        await archiveTicket({
          variables: { input: { ticketId } },
          update() {},
        });
        succeeded.push(ticketId);
      } catch (err) {
        failed.push({ id: ticketId, reason: err?.message });
      }
    }

    ticketsWithSubs.forEach((t) => {
      if (!succeeded.includes(t._id)) return;
      (t.subWorkOrders || []).forEach((sub) => {
        if (sub?._id && cascadedChildIds.has(sub._id)) {
          succeeded.push(sub._id);
        }
      });
    });

    evictTicketListCaches(client.cache);
    return { succeeded, failed };
  };

  return { bulkArchiveTickets };
};

export const useBulkDeleteTickets = () => {
  const [bulkDelete] = useMutation(BULK_DELETE_OWN_OEM_TICKETS_MUTATION);

  const bulkDeleteTickets = async (ticketIds, deleteSubWorkOrders = false) => {
    const { data } = await bulkDelete({
      variables: { ticketIds, deleteSubWorkOrders },
      update(cache) {
        evictTicketListCaches(cache);
      },
    });
    return data.bulkDeleteOwnOemTickets;
  };

  return { bulkDeleteTickets };
};
